import React, { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import CrudTable from "../components/CrudTable";
import { Scale, CheckCircle2, ClipboardList, AlertTriangle } from "lucide-react";

const VARDIYA = ["00:00 - 08:00", "08:00 - 16:00", "16:00 - 24:00"];
const fmt = (n) => (n == null ? "0" : Number(n).toLocaleString("tr-TR"));

const REASONS = [
  ["kenar_catlak", "Kenar Çatlak"], ["ayak_kirik", "Ayak Kırık"], ["demir_leke", "Demir Leke"], ["capak", "Çapak"],
  ["kirik_sirlama", "Kırık Sırlama"], ["boya_puskurtme", "Boyalı/Boya Püskürtme"], ["rotus_hatasi", "Rötuş Hatası"],
  ["hamur_havali", "Hamur Havalı"], ["sirsiz", "Sırsız"], ["ayak_catlak", "Ayak Çatlak"], ["tozlu_sirlama", "Tozlu Sırlama"],
  ["deform", "Deform"], ["kulp_acik", "Kulp Açık"], ["kalip_hatasi", "Kalıp Hatası"], ["cokme", "Çökme"],
  ["sir_yirtilma", "Sır Yırtılma"], ["pinho", "Pinho"], ["puskurtme", "Püskürtme"], ["sir_atma", "Sır Atma"],
  ["reduksiyon", "Redüksiyon"], ["sir_deligi", "Sır Deliği"], ["sir_acigi_carpma", "Sır Açığı/Çarpma"],
  ["sir_birikme", "Sır Birikme"], ["tamir", "Tamir"], ["sir_akma_birikme", "Sır Akma/Birikme"],
];

const KG_KEYS = [
  ["kalite1", "1.Kalite", "text-green-700"], ["kalite2", "2.Kalite", "text-sky-700"],
  ["kalite3", "3.Kalite", "text-amber-700"], ["fire", "Fire", "text-red-600"],
  ...REASONS.map(([k, l]) => [k, l, "text-zinc-600"]),
];

function Kpi({ icon: Icon, label, value, tone = "slate", testid }) {
  const tones = { amber: "text-amber-600 bg-amber-50", green: "text-green-600 bg-green-50", blue: "text-sky-600 bg-sky-50", slate: "text-zinc-700 bg-zinc-100" };
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</span>
        <span className={`p-1 rounded-sm ${tones[tone]}`}><Icon size={14} /></span>
      </div>
      <div className="font-head font-black text-2xl mt-1 num">{value}</div>
    </div>
  );
}

function KaliteGunluk() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [urunKodu, setUrunKodu] = useState("");
  const [d, setD] = useState(null);

  const load = (s, e, u) => {
    const p = {};
    if (s) p.start = s;
    if (e) p.end = e;
    if (u) p.urun_kodu = u;
    api.get("/dashboard/kalite-gunluk", { params: p }).then((r) => setD(r.data)).catch(() => {});
  };
  useEffect(() => { load("", "", ""); }, []);

  const t = d?.totals || {};
  return (
    <div className="border border-zinc-200 bg-white rounded-sm p-4 m-6 mt-0" data-testid="kalite-gunluk-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-head font-bold text-sm uppercase tracking-wide text-zinc-700">Üretim Kalite — Geçen Adet</h3>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="block text-[10px] font-semibold text-zinc-500 uppercase mb-0.5">Ürün Kodu</label>
            <input type="text" value={urunKodu} onChange={(e) => setUrunKodu(e.target.value)} data-testid="kalite-urun-kodu"
              placeholder="Ürün kodu..." className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono w-40" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-zinc-500 uppercase mb-0.5">Başlangıç</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="kalite-start"
              className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono" />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-zinc-500 uppercase mb-0.5">Bitiş</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="kalite-end"
              className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm mono" />
          </div>
          <button onClick={() => load(start, end, urunKodu)} data-testid="kalite-filter-btn"
            className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-3 py-1.5 rounded-sm">Filtrele</button>
          <button onClick={() => { setStart(""); setEnd(""); setUrunKodu(""); load("", "", ""); }} data-testid="kalite-clear-btn"
            className="border border-zinc-300 hover:bg-zinc-50 text-zinc-600 text-sm font-semibold px-3 py-1.5 rounded-sm">Temizle</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
        {KG_KEYS.map(([k, label, cls]) => (
          <div key={k} className="border border-zinc-200 rounded-sm p-2.5" data-testid={`kalite-box-${k}`}>
            <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide">{label}</div>
            <div className={`font-head font-black text-2xl num ${cls}`}>{fmt(t[k])}</div>
          </div>
        ))}
        <div className="border-2 border-zinc-800 rounded-sm p-2.5 bg-zinc-50">
          <div className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Toplam Adet</div>
          <div className="font-head font-black text-2xl num text-zinc-900">{fmt(d?.toplam)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="grid grid-cols-2 gap-2">
          <Kpi icon={Scale} label="Toplam Geçen KG" value={`${fmt(d?.toplam_kg)} kg`} tone="slate" testid="kpi-toplam-kg" />
          <Kpi icon={CheckCircle2} label="1. Kalite Oranı" value={`%${d?.kalite_yuzde ?? 0}`} tone="green" testid="kpi-kalite-yuzde" />
          <Kpi icon={ClipboardList} label="FASON Geçen" value={fmt(d?.fason)} tone="amber" testid="kpi-fason-gecen" />
          <Kpi icon={ClipboardList} label="HORECA Geçen" value={fmt(d?.horeca)} tone="blue" testid="kpi-horeca-gecen" />
        </div>
        <div className="md:col-span-2 border border-zinc-200 rounded-sm overflow-hidden">
          <div className="px-3 py-2 bg-zinc-800 text-white text-xs font-semibold uppercase tracking-wide">Fırın Bölgesinden Geçen Adet</div>
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full data-table" data-testid="firin-table">
              <thead className="sticky top-0"><tr><th>Fırın Bölgesi</th><th className="text-right">Geçen Adet</th></tr></thead>
              <tbody>
                {(d?.firin || []).length === 0 ? (
                  <tr><td colSpan={2} className="text-center py-6 text-zinc-400">Kayıt yok.</td></tr>
                ) : d.firin.map((f, i) => (
                  <tr key={i}><td>{f.bolge}</td><td className="num font-semibold">{fmt(f.adet)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function EksikUrunUyari() {
  const [codes, setCodes] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/entity/quality"), api.get("/products")])
      .then(([q, p]) => {
        const known = new Set((p.data || []).map((x) => (x.urun_kodu || "").trim()).filter(Boolean));
        const seen = new Set();
        const missing = [];
        (q.data || []).forEach((r) => {
          const c = (r.urun_kodu || "").trim();
          if (c && !known.has(c) && !seen.has(c)) { seen.add(c); missing.push(c); }
        });
        setCodes(missing);
      }).catch(() => {});
  }, []);
  if (codes.length === 0) return null;
  return (
    <div className="mb-3 flex items-start gap-2 border-l-4 border-red-500 bg-red-50 text-red-700 rounded-sm px-4 py-3" data-testid="quality-missing-warning">
      <AlertTriangle size={18} className="shrink-0 mt-0.5" />
      <div className="text-sm">
        <span className="font-bold">Uyarı: Eksik veri var!</span>{" "}
        <span className="font-semibold">Bazı kalite kayıtlarındaki ürünler Malzemeler'de tanımlı değil — Kalıp Kodu / Renk otomatik gelmiyor.</span>
        <div className="mt-1 flex flex-wrap gap-1">
          {codes.slice(0, 30).map((c, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-white border border-red-200 rounded-sm px-1.5 py-0.5 text-xs font-semibold mono">{c}</span>
          ))}
          {codes.length > 30 && <span className="text-xs text-red-500">+{codes.length - 30} daha</span>}
        </div>
      </div>
    </div>
  );
}


export default function Quality() {
  const reasonCols = REASONS.map(([k, l]) => ({ key: k, label: l, num: true }));
  const columns = [
    { key: "tarih", label: "Tarih" }, { key: "vardiya", label: "Vardiya" },
    { key: "urun_kodu", label: "Ürün Kodu", mono: true },
    { key: "kalite1", label: "1.Kalite", num: true }, { key: "kalite2", label: "2.Kalite", num: true },
    { key: "kalite3", label: "3.Kalite", num: true }, { key: "fire", label: "Fire", num: true },
    {
      key: "toplam", label: "Toplam", num: true,
      value: (r) => ["kalite1", "kalite2", "kalite3", "fire"].reduce((s, k) => s + (Number(r[k]) || 0), 0),
      render: (r) => {
        const t = ["kalite1", "kalite2", "kalite3", "fire"].reduce((s, k) => s + (Number(r[k]) || 0), 0);
        return <span className="font-semibold text-indigo-700">{t.toLocaleString("tr-TR")}</span>;
      },
    },
    ...reasonCols,
  ];
  const reasonFields = REASONS.map(([k, l]) => ({ key: k, label: l, type: "number" }));
  const fields = [
    { key: "tarih", label: "Tarih", type: "date", required: true },
    { key: "vardiya", label: "Vardiya", type: "select", options: VARDIYA, required: true },
    { key: "urun_kodu", label: "Ürün Kodu", type: "product", required: true, span: 2, fills: { kalip_kodu: "kalip_kodu", aciklama: "aciklama" } },
    { key: "kalite1", label: "1. Kalite", type: "number" },
    { key: "kalite2", label: "2. Kalite", type: "number" },
    { key: "kalite3", label: "3. Kalite", type: "number" },
    { key: "fire", label: "Fire", type: "number" },
    ...reasonFields,
  ];
  return (
    <>
      <PageHeader title="Kalite Takibi" subtitle="Kalite sınıfları ve defo (fire) kayıtları" />
      <div className="px-6 pt-4"><EksikUrunUyari /></div>
      <CrudTable endpoint="/entity/quality" columns={columns} fields={fields} titleSingular="Kalite Kaydı"
        dateField="tarih" vardiyaFilter exportKind="quality"
        bulkEditFields={["vardiya", "kalite1", "kalite2", "kalite3", "fire", ...REASONS.map(([k]) => k)]} />
      <KaliteGunluk />
    </>
  );
}
